import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getRSVPInviteBySlug, searchGuests } from '../../services/rsvpInviteService'
import { showAlert } from '../../services/dialogService'
import './RSVPSearch.css'

function RSVPSearch() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [invite, setInvite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [guests, setGuests] = useState([])
  const [selectedGuest, setSelectedGuest] = useState(null)
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    const loadInvite = async () => {
      try {
        setLoading(true)
        const inviteData = await getRSVPInviteBySlug(slug)
        if (!inviteData) {
          setLoading(false)
          return
        }
        setInvite(inviteData)
      } catch (error) {
        console.error('Error loading invite:', error)
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      loadInvite()
    }
  }, [slug])

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      await showAlert('Please enter a name or email to search.')
      return
    }

    // Usar wedding_id directamente del invite, no de weddings (que puede fallar por RLS)
    const weddingId = invite?.wedding_id || invite?.weddings?.id
    if (!weddingId) {
      console.error('No wedding ID found in invite:', invite)
      await showAlert('Error: Could not find wedding information. Please try again later.')
      return
    }

    try {
      setSearching(true)
      const results = await searchGuests(weddingId, searchTerm)
      setGuests(results)
    } catch (error) {
      console.error('RSVPSearch: Error searching guests:', error)
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      })
      
      // Mostrar mensaje más específico según el error
      if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('policy')) {
        await showAlert('Error: Permission denied. Please contact the event organizers. The search feature may not be properly configured.')
      } else {
        await showAlert(`Error searching for guests: ${error.message || 'Please try again.'}`)
      }
    } finally {
      setSearching(false)
    }
  }

  const handleContinue = () => {
    if (selectedGuest) {
      navigate(`/rsvp/${slug}/form/${selectedGuest.id}`)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    } catch {
      return dateString
    }
  }

  if (loading) {
    return (
      <div className="rsvp-search-page">
        <div className="rsvp-search-container">
          <div className="rsvp-loading">Loading...</div>
        </div>
      </div>
    )
  }

  if (!invite) {
    return (
      <div className="rsvp-search-page">
        <div className="rsvp-search-container">
          <div className="rsvp-error">Invitation not found</div>
        </div>
      </div>
    )
  }

  const coupleNames = invite.partner2_name 
    ? `${invite.partner1_name} & ${invite.partner2_name}`
    : invite.partner1_name

  return (
    <div className="rsvp-search-page">
      <div className="rsvp-search-container">
        <div className="rsvp-search-header">
          <img src="/img/logo-prueba.png" alt="The UNFILTERED COLLECTIVE" className="rsvp-logo" />
        </div>

        <div className="rsvp-search-content">
          <div className="rsvp-search-instructions">
            <p>
              To RSVP, search by your first name, last name, or email and hit Search. Your invitation will pop up, 
              and you can let us know if you'll be there to celebrate with us.
            </p>
          </div>

          <div className="rsvp-search-form">
            <div className="rsvp-search-inputs">
              <input
                type="text"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="rsvp-search-input"
              />
            </div>
            <button 
              className="rsvp-search-btn"
              onClick={handleSearch}
              disabled={searching || !searchTerm.trim()}
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {invite && (
            <div className="rsvp-couple-info">
              <div className="rsvp-couple-names">{coupleNames.toUpperCase()}</div>
              {invite.wedding_date && (
                <div className="rsvp-couple-date">{formatDate(invite.wedding_date)}</div>
              )}
            </div>
          )}

          {guests.length > 0 && (
            <div className="rsvp-guest-selection">
              <h3>PLEASE, SELECT YOUR NAME</h3>
              <div className="rsvp-guest-list">
                {guests.map((guest) => (
                  <label 
                    key={guest.id} 
                    className={`rsvp-guest-item ${selectedGuest?.id === guest.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="guest"
                      value={guest.id}
                      checked={selectedGuest?.id === guest.id}
                      onChange={() => setSelectedGuest(guest)}
                    />
                    <span>{guest.first_name} {guest.last_name}</span>
                  </label>
                ))}
              </div>
              <div className="rsvp-guest-actions">
                <button 
                  className="rsvp-continue-btn"
                  onClick={handleContinue}
                  disabled={!selectedGuest}
                >
                  Continue
                </button>
                <button 
                  className="rsvp-new-search-btn"
                  onClick={() => {
                    setSearchTerm('')
                    setGuests([])
                    setSelectedGuest(null)
                  }}
                >
                  New search
                </button>
              </div>
            </div>
          )}

          {guests.length === 0 && searchTerm && !searching && (
            <div className="rsvp-no-results">
              <p>No guests found. Please check your name and try again.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RSVPSearch
